// Logica condivisa dell'editor lettere: intestazione studio, firma e generazione PDF.
// Usata sia da GestioneLettereDialog (editor lettere) sia dal composer Email/PEC, senza duplicare codice.
import { encodeHtmlCopy } from "./pdfToHtml";


// Rimuove dall'HTML (contenuto lettera incollato da Word) i residui VML/Office che i client
// email mostrano come testo (es. "v\\:* {behavior:url(#default#VML);}"): commenti condizionali,
// blocchi <xml>/<style>, tag namespace (o:, v:, w:) e regole CSS VML residue.
export function sanitizeEmailHtml(html) {
  if (!html) return "";
  return String(html)
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<xml[\s\S]*?<\/xml>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<\/?[a-z]+:[^>]*>/gi, "")
    .replace(/behavior\s*:\s*url\(#default#VML\)\s*;?/gi, "")
    .replace(/[a-z]+\\?:\*?\s*\{[^}]*\}/gi, "");
}

export function studioLines(d) {
  const s = d || {};
  const rows = [];
  const prov = (s.provincia || "").trim();
  const citta0 = (s.citta || "").trim();
  const cityProv = prov && citta0 && !citta0.includes("(") ? `${citta0} (${prov})` : citta0;
  const r2 = [s.indirizzo, cityProv].map((x) => (x || "").trim()).filter(Boolean).join(" - ");
  if (r2) rows.push(r2);
  const r3 = [s.partita_iva ? `P.iva ${s.partita_iva}` : "", s.codice_fiscale ? `cf ${s.codice_fiscale}` : ""].filter(Boolean).join(" - ");
  if (r3) rows.push(r3);
  const r4 = [s.cellulare ? `Cell. ${s.cellulare}` : "", s.telefono ? `Tel. ${s.telefono}` : ""].filter(Boolean).join(" - ");
  if (r4) rows.push(r4);
  const r5 = [s.email ? `E-mail: ${s.email}` : "", s.pec ? `PEC: ${s.pec}` : ""].filter(Boolean).join(" - ");
  if (r5) rows.push(r5);
  return rows;
}

// Intestazione studio per il corpo email (stili inline, tabella per compatibilità client). "" se assente.
// Variabili inseribili nell'intestazione personalizzata (Impostazioni studio).
export const HEAD_VARS = [
  { key: "nome", label: "Nome studio" }, { key: "indirizzo", label: "Indirizzo" }, { key: "cap", label: "CAP" },
  { key: "comune", label: "Comune" }, { key: "provincia", label: "Provincia" }, { key: "partita_iva", label: "P.IVA" },
  { key: "telefono", label: "Telefono" }, { key: "cellulare", label: "Cellulare" }, { key: "email", label: "Email" }, { key: "pec", label: "PEC" },
  { key: "numero_civico", label: "Numero civico" }, { key: "fax", label: "Fax" }, { key: "sito_web", label: "Sito web" },
  { key: "codice_fiscale", label: "Codice Fiscale" }, { key: "albo", label: "N. iscrizione albo" }, { key: "iban", label: "IBAN" },
  { key: "codice_studio", label: "Codice Studio" },
  { key: "titolare_nome", label: "Nome titolare" }, { key: "titolare_cognome", label: "Cognome titolare" },
  { key: "titolare", label: "Nome e cognome titolare" }, { key: "titolare_data_nascita", label: "Data nascita titolare" },
  { key: "titolare_luogo_nascita", label: "Luogo nascita titolare" }, { key: "titolare_luogo_nascita_prov", label: "Prov. nascita titolare" },
];

// s = { studio_name, ...settings }
export function headVarValues(s) {
  const v = s || {};
  return {
    nome: (v.studio_name || (v.intestazione || "").split("\n")[0] || "").trim(),
    indirizzo: v.indirizzo || "", cap: v.cap || "", comune: v.citta || "", provincia: v.provincia || "",
    partita_iva: v.partita_iva || "", telefono: v.telefono || "", cellulare: v.cellulare || "",
    email: v.email_contatto || "", pec: v.pec || "",
    numero_civico: v.numero_civico || "", fax: v.fax || "", sito_web: v.sito_web || "",
    codice_fiscale: v.codice_fiscale || "", albo: v.albo || "", iban: v.iban || "", codice_studio: v.codice_studio || "",
    titolare_nome: v.owner_nome || "", titolare_cognome: v.owner_cognome || "",
    titolare: [v.owner_nome, v.owner_cognome].filter(Boolean).join(" "),
    titolare_data_nascita: String(v.owner_data_nascita || "").replace(/^(\d{4})-(\d{2})-(\d{2}).*$/, "$3/$2/$1"),
    titolare_luogo_nascita: v.owner_luogo_nascita || "", titolare_luogo_nascita_prov: v.owner_luogo_nascita_prov || "",
  };
}

// Sostituisce {{studio.xxx}} nel template HTML dell'intestazione. "" se il template è vuoto.
export function renderHeadTemplate(tpl, s) {
  if (!tpl || !String(tpl).replace(/<[^>]+>/g, "").trim()) return "";
  const vals = headVarValues(s);
  const esc = (x) => String(x || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return String(tpl).replace(/\{\{\s*studio\.(\w+)\s*\}\}/g, (m, k) => (k in vals ? esc(vals[k]) : m));
}

// Intestazione personalizzata -> HTML email con stili inline (paragrafi senza margini, righe vuote preservate).
function customHeadEmail(html) {
  if (typeof document === "undefined") return html;
  const root = document.createElement("div");
  root.innerHTML = restoreBlankLines(html);
  root.querySelectorAll("p").forEach((el) => {
    const st = (el.getAttribute("style") || "").trim().replace(/;$/, "");
    el.setAttribute("style", `margin:0${st ? ";" + st : ""}`);
  });
  root.querySelectorAll("strong,b").forEach((el) => el.setAttribute("style", `font-weight:bold;${el.getAttribute("style") || ""}`));
  root.querySelectorAll("em,i").forEach((el) => el.setAttribute("style", `font-style:italic;${el.getAttribute("style") || ""}`));
  return root.innerHTML;
}

export const headAlign = (inte) => (["left", "center", "right"].includes(inte?.align) ? inte.align : "left");

export function emailHeaderHtml(inte) {
  if (inte?.html) {
    const Wc = LETTER_CONTENT_PX;
    return `<table role="presentation" width="${Wc}" cellpadding="0" cellspacing="0" border="0" align="center" style="border-collapse:collapse;width:${Wc}px;max-width:100%;margin:0 auto 14px auto">`
      + `<tr><td style="padding:0 0 4px 0;border-bottom:1px solid #111111;font-family:'Inter',Arial,Helvetica,sans-serif;font-size:13px;color:#111827;line-height:1.15;text-align:left">${customHeadEmail(inte.html)}</td></tr></table>`;
  }
  if (!inte || (!inte.nome && !(inte.lines || []).length)) return "";
  const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const W = LETTER_CONTENT_PX;
  const al = headAlign(inte);
  const lines = (inte.lines || []).map((l) => `<div style="font-size:12px;line-height:1.15;color:#333333;margin:0">${esc(l)}</div>`).join("");
  return `<table role="presentation" width="${W}" cellpadding="0" cellspacing="0" border="0" align="center" style="border-collapse:collapse;width:${W}px;max-width:100%;margin:0 auto 14px auto">`
    + `<tr><td align="${al}" style="padding:0 0 4px 0;border-bottom:1px solid #111111;font-family:'Inter',Arial,Helvetica,sans-serif;text-align:${al};color:#111827;line-height:1.15">`
    + (inte.nome ? `<div style="font-size:13px;font-weight:bold;line-height:1.15;color:#111827;margin:0 0 1px 0">${esc(inte.nome)}</div>` : "")
    + lines + `</td></tr></table>`;
}

export function sediList(studio) {
  if (!studio) return [];
  const s = studio.settings || {};
  const principal = {
    key: "principal",
    label: (studio.studio_name || (s.intestazione || "").split("\n")[0] || "Studio principale").trim(),
    nome: (studio.studio_name || (s.intestazione || "").split("\n")[0] || "").trim(),
    lines: studioLines({ indirizzo: s.indirizzo, citta: s.citta, provincia: s.provincia, partita_iva: s.partita_iva, codice_fiscale: s.codice_fiscale, telefono: s.telefono, cellulare: s.cellulare, email: s.email_contatto, pec: s.pec }),
    align: s.intestazione_align || "left",
    html: renderHeadTemplate(s.intestazione_custom_html, { studio_name: studio.studio_name, ...s }),
  };
  const assoc = (Array.isArray(s.studi_associati) ? s.studi_associati : []).map((a, i) => ({
    key: `assoc-${i}`,
    label: (a.nome || `Studio associato ${i + 1}`).trim(),
    nome: (a.nome || "").trim(),
    lines: studioLines(a),
    align: s.intestazione_align || "left",
  }));
  return [principal, ...assoc];
}

export function buildSignatureText(studio) {
  if (!studio) return "";
  const s = studio.settings || {};
  const custom = (s.firma_email || "").trim();
  if (custom) return custom;
  const name = (studio.studio_name || s.intestazione || "").trim().split("\n")[0].trim();
  const _prov = (s.provincia || "").trim();
  const _citta = (s.citta || "").trim();
  const _cityProv = _prov && _citta && !_citta.includes("(") ? `${_citta} (${_prov})` : _citta;
  const addr = [s.indirizzo, _cityProv].map((x) => (x || "").trim()).filter(Boolean).join(", ");
  const vals = {
    nome: name,
    indirizzo: addr,
    telefono: s.telefono ? `Tel. ${s.telefono}` : "",
    cellulare: s.cellulare ? `Cell. ${s.cellulare}` : "",
    fax: s.fax ? `Fax ${s.fax}` : "",
    email: (s.email_contatto || "").trim(),
    pec: s.pec ? `PEC: ${s.pec}` : "",
    sito_web: (s.sito_web || "").trim(),
    partita_iva: s.partita_iva ? `P.IVA ${s.partita_iva}` : "",
    codice_fiscale: s.codice_fiscale ? `C.F. ${s.codice_fiscale}` : "",
    albo: s.albo ? `Iscr. Albo ${s.albo}` : "",
  };
  const order = ["nome", "indirizzo", "telefono", "cellulare", "fax", "email", "pec", "sito_web", "partita_iva", "codice_fiscale", "albo"];
  let sel = s.firma_fields;
  if (!Array.isArray(sel)) sel = order.filter((k) => vals[k]);
  return order.filter((k) => sel.includes(k)).map((k) => vals[k]).filter(Boolean).join("\n");
}

export const LETTER_CSS = `
    .pgp-pdfroot{font-family:'Inter',Arial,Helvetica,sans-serif;color:#111827;font-size:14px;line-height:1.7;background:#fff;box-sizing:border-box;white-space:pre-wrap;max-width:100%;overflow-wrap:break-word;word-break:break-word}
    .pgp-pdfroot *{box-sizing:border-box}
    .pgp-pdfroot .pgp-doc,.pgp-pdfroot .pgp-doc *{max-width:100%}
    .pgp-pdfroot img{max-width:100%;height:auto}
    .pgp-pdfroot .pgp-letterhead{border-bottom:1px solid #111;padding-bottom:4px;margin-bottom:14px;line-height:1.15;color:#111827;white-space:normal}
    .pgp-pdfroot .pgp-letterhead .lh-name{font-size:13px;font-weight:700}
    .pgp-pdfroot .pgp-letterhead .lh-line{font-size:12px;color:#333;line-height:1.15}
    .pgp-pdfroot .pgp-letterhead p{margin:0}
    .pgp-pdfroot .pgp-doc{white-space:pre-wrap}
    .pgp-pdfroot .pgp-doc p{margin:0;white-space:pre-wrap}
    .pgp-pdfroot .pgp-doc p:last-child{margin-bottom:0}
    .pgp-pdfroot .pgp-doc h1,.pgp-pdfroot .pgp-doc h2,.pgp-pdfroot .pgp-doc h3,.pgp-pdfroot .pgp-doc h4{margin:0 0 0.4em;line-height:1.25}
    .pgp-pdfroot .pgp-doc strong,.pgp-pdfroot .pgp-doc b{font-weight:normal;text-shadow:0.03em 0 0 currentColor,-0.03em 0 0 currentColor}
    .pgp-pdfroot .pgp-doc em{font-style:italic}
    .pgp-pdfroot .pgp-doc u{text-decoration:underline}
    .pgp-pdfroot .pgp-doc ul{list-style:disc;padding-left:1.4em;margin:0.4em 0}
    .pgp-pdfroot .pgp-doc ol{list-style:decimal;padding-left:1.4em;margin:0.4em 0}
    .pgp-pdfroot .pgp-doc table{border-collapse:collapse;width:100%;margin:0.6em 0;table-layout:fixed}
    .pgp-pdfroot .pgp-doc td,.pgp-pdfroot .pgp-doc th{border:1px solid #9ca3af;padding:6px 8px;vertical-align:top}
    .pgp-pdfroot .pgp-doc td[style*="border-style:none"],.pgp-pdfroot .pgp-doc th[style*="border-style:none"],.pgp-pdfroot .pgp-doc table[style*="border-style:none"]{border:0 !important}
    .pgp-pdfroot .pgp-doc td[style*="border-width:0"],.pgp-pdfroot .pgp-doc th[style*="border-width:0"]{border:0 !important}
    .pgp-pdfroot .pgp-doc th{background:rgba(0,0,0,0.05);font-weight:600}
    .pgp-pdfroot .pgp-signature{margin-top:32px;white-space:pre-wrap;font-size:14px;color:#111827;line-height:1.7}`;

// I paragrafi/intestazioni vuoti (righe inserite con "invio a capo", es. il riquadro schizzo)
// collassano in fase di rendering PDF/anteprima: getHTML() di TipTap non include il <br> finale
// che l'editor mostra, quindi la riga vuota perde l'altezza. Reinseriamo il <br> così ogni riga
// vuota conserva l'altezza di riga, identica all'editor (vale anche per le celle del box schizzo).
export function restoreBlankLines(html) {
  if (!html) return "";
  return String(html).replace(/<(p|h[1-6])((?:\s[^>]*)?)>(\s*)<\/\1>/gi, "<$1$2><br></$1>");
}

// Soglia tipografica: una riga con meno di queste parole viene allineata a sinistra
// anziché giustificata (evita gli spazi enormi su righe corte tipo "SEDE LEGALE").
export const JUSTIFY_MAX_WORDS = 5;

const _countWords = (nodes) => {
  const text = nodes.map((n) => n.textContent || "").join(" ");
  return (text.trim().match(/\S+/g) || []).length;
};

// Comportamento tipografico standard per il testo giustificato: le righe corte
// (meno di JUSTIFY_MAX_WORDS parole) e l'ultima riga di ogni paragrafo vengono
// allineate a sinistra invece che giustificate, mantenendo la giustificazione sulle
// righe piene. Le righe interne separate da <br> diventano blocchi per poter avere
// un allineamento indipendente. Applicato a PDF e anteprime.
export function applyJustifyTypography(html) {
  if (!html || typeof document === "undefined") return html || "";
  const root = document.createElement("div");
  root.innerHTML = String(html);
  root.querySelectorAll('[style*="justify"]').forEach((el) => {
    const style = el.getAttribute("style") || "";
    if (!/text-align\s*:\s*justify/i.test(style)) return;
    // Raggruppa i nodi figli in righe separate dai <br>.
    const groups = [];
    let current = [];
    Array.from(el.childNodes).forEach((n) => {
      if (n.nodeType === 1 && n.tagName === "BR") {
        groups.push(current);
        current = [];
      } else {
        current.push(n);
      }
    });
    groups.push(current);
    if (groups.length <= 1) {
      // Riga singola: giustifica solo se abbastanza lunga, altrimenti a sinistra.
      if (_countWords(groups[0]) < JUSTIFY_MAX_WORDS) {
        el.setAttribute("style", style.replace(/text-align\s*:\s*justify/i, "text-align:left"));
      }
      return;
    }
    // Più righe: ricostruisce ogni riga come blocco con il proprio allineamento.
    const frag = document.createDocumentFragment();
    groups.forEach((nodes, idx) => {
      const isLast = idx === groups.length - 1;
      const left = isLast || _countWords(nodes) < JUSTIFY_MAX_WORDS;
      const span = document.createElement("span");
      span.setAttribute("style", `display:block;text-align:${left ? "left" : "justify"}${idx > 0 ? ";text-indent:0" : ""}`);
      if (nodes.length === 0) span.appendChild(document.createElement("br"));
      else nodes.forEach((n) => span.appendChild(n));
      frag.appendChild(span);
    });
    el.textContent = "";
    el.appendChild(frag);
    el.setAttribute("style", style.replace(/text-align\s*:\s*justify/i, "text-align:left"));
  });
  return root.innerHTML;
}

// Larghezza del contenuto della lettera reale: pagina A4 (210mm) meno i margini standard
// (sx 2.5cm, dx 2cm) = 165mm ≈ 624px a 96dpi. Usata per vincolare l'anteprima nel corpo email.
export const LETTER_CONTENT_PX = 624;

// Converte l'HTML della lettera (TipTap) in HTML email-safe con stili INLINE: i client
// email ignorano i blocchi <style> e le classi CSS, quindi replichiamo qui, su ogni
// elemento, le stesse regole di .pgp-doc (margini paragrafo, pre-wrap per spaziature e
// rientri, grassetto/corsivo/sottolineato, allineamenti, liste e tabelle) così il corpo
// ricevuto corrisponde fedelmente all'anteprima della lettera.
export function letterHtmlToEmail(html) {
  if (!html) return "";
  const prepared = applyJustifyTypography(sanitizeEmailHtml(restoreBlankLines(html)));
  if (typeof document === "undefined") return prepared;
  const root = document.createElement("div");
  root.innerHTML = prepared;
  const prepend = (el, css) => {
    const existing = (el.getAttribute("style") || "").trim().replace(/;$/, "");
    el.setAttribute("style", css + (existing ? ";" + existing : ""));
  };
  root.querySelectorAll("p").forEach((el) => prepend(el, "margin:0;white-space:pre-wrap"));
  root.querySelectorAll("h1,h2,h3,h4,h5,h6").forEach((el) => prepend(el, "margin:0 0 0.4em;line-height:1.25"));
  root.querySelectorAll("strong,b").forEach((el) => prepend(el, "font-weight:bold"));
  root.querySelectorAll("em,i").forEach((el) => prepend(el, "font-style:italic"));
  root.querySelectorAll("u").forEach((el) => prepend(el, "text-decoration:underline"));
  root.querySelectorAll("ul").forEach((el) => prepend(el, "list-style:disc;padding-left:1.4em;margin:0.4em 0"));
  root.querySelectorAll("ol").forEach((el) => prepend(el, "list-style:decimal;padding-left:1.4em;margin:0.4em 0"));
  const SHEET = 640, PAD_X = 36, PAD_Y = 40;
  const W = SHEET - PAD_X * 2;
  const toPx = (w) => {
    const v = parseFloat(w);
    if (!w || isNaN(v)) return 0;
    return /%$/.test(String(w).trim()) ? (v / 100) * W : v;
  };
  root.querySelectorAll("table").forEach((el) => {
    const st = el.getAttribute("style") || "";
    const borderless = /border-style\s*:\s*none/i.test(st) || /border-width\s*:\s*0/i.test(st);
    // Larghezza fissa + border-collapse: struttura a colonne che i client email (Outlook incluso)
    // renderizzano correttamente solo come tabella, non con flexbox/grid.
    el.style.removeProperty("min-width");
    el.style.removeProperty("width");
    prepend(el, `border-collapse:collapse;table-layout:fixed;width:100%;max-width:${W}px;margin:0.6em 0`);
    el.setAttribute("width", String(W));
    el.setAttribute("cellpadding", "0");
    el.setAttribute("cellspacing", "0");
    el.setAttribute("border", "0");
    // Molti client email ignorano <colgroup>: propaghiamo le larghezze delle colonne sulle
    // celle della prima riga così le colonne mantengono le proporzioni originali.
    const cols = Array.from(el.querySelectorAll(":scope > colgroup > col"));
    const firstRow = el.querySelector("tr");
    const cells = firstRow ? Array.from(firstRow.children).filter((c) => /^(TD|TH)$/.test(c.tagName)) : [];
    if (cells.length) {
      // Solo "width" esplicito (non "min-width" di TipTap, che renderebbe le colonne strettissime);
      // fallback su colwidth di TipTap o colonne di pari larghezza.
      // Larghezze in px esplicite su TUTTE le colonne (somma = larghezza lettera): le colonne senza
      // larghezza si dividono lo spazio restante, così i client non comprimono il destinatario.
      const raw = cells.map((cell, i) => {
        const c = cols[i];
        const m = c ? (c.getAttribute("style") || "").match(/(?:^|;)\s*width\s*:\s*([^;]+)/i) : null;
        return toPx((m ? m[1].trim() : (c && c.getAttribute("width"))) || cell.getAttribute("colwidth") || "");
      });
      const known = raw.reduce((s, w) => s + w, 0);
      const missing = raw.filter((w) => !w).length;
      const rest = missing ? Math.max(0, W - known) / missing : 0;
      let px = raw.map((w) => w || rest);
      const tot = px.reduce((s, w) => s + w, 0);
      if (!tot || (missing && rest < 40)) px = cells.map(() => W / cells.length);
      else if (tot !== W) px = px.map((w) => (w * W) / tot);
      px = px.map((w) => Math.floor(w));
      cells.forEach((cell, i) => {
        cell.style.removeProperty("width");
        cell.style.removeProperty("min-width");
        prepend(cell, `width:${((px[i] / W) * 100).toFixed(2)}%`);
        cell.setAttribute("width", String(px[i]));
        cell.removeAttribute("colwidth");
      });
      cols.forEach((c, i) => { if (px[i]) { c.setAttribute("style", `width:${px[i]}px`); c.setAttribute("width", String(px[i])); } });
    }
    if (borderless) el.setAttribute("role", "presentation");
  });
  root.querySelectorAll("td,th").forEach((el) => {
    const st = el.getAttribute("style") || "";
    const noBorder = /border-style\s*:\s*none/i.test(st) || /border-width\s*:\s*0/i.test(st);
    prepend(el, `${noBorder ? "border:0" : "border:1px solid #9ca3af"};padding:6px 8px;vertical-align:top;text-align:left;white-space:normal;word-break:normal;overflow-wrap:normal;word-wrap:normal;hyphens:none`);
  });
  // Nelle celle i paragrafi mantengono le righe della lettera ma non spezzano le parole (nomi destinatari).
  root.querySelectorAll("td p, th p").forEach((el) => {
    const st = (el.getAttribute("style") || "").trim().replace(/;$/, "");
    el.setAttribute("style", `${st ? st + ";" : ""}white-space:normal;word-break:normal;overflow-wrap:normal`);
  });
  root.querySelectorAll("img").forEach((el) => prepend(el, "max-width:100%;height:auto"));
  // Molti client (Gmail, Outlook) ignorano white-space:pre-wrap: a capo e spazi multipli del
  // testo diventano <br> e &nbsp; espliciti, e le righe vuote ricevono un &nbsp; per non collassare.
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const texts = [];
  while (walker.nextNode()) texts.push(walker.currentNode);
  texts.forEach((t) => {
    const v = t.nodeValue || "";
    if (!/\n| {2,}|\t|^ | $/.test(v)) return;
    const span = document.createElement("span");
    span.innerHTML = v.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/\t/g, "    ")
      .replace(/ {2,}/g, (m) => "&nbsp;".repeat(m.length - 1) + " ").replace(/^ /, "&nbsp;").replace(/\r?\n/g, "<br>");
    t.replaceWith(...span.childNodes);
  });
  root.querySelectorAll("p,h1,h2,h3,h4,h5,h6").forEach((el) => {
    if (!(el.textContent || "").trim() && !el.querySelector("img,table")) el.innerHTML = "&nbsp;";
  });
  // Larghezza del contenuto identica alla pagina A4 della lettera reale (210mm - margini 2.5/2 cm
  // = 165mm ≈ 624px): evita che il testo risulti troppo largo e preserva le proporzioni delle
  // colonne (tabella) come nell'anteprima/PDF. Tabella wrapper a larghezza fissa per Outlook.
  const inner = `<div style="font-family:'Inter',Arial,Helvetica,sans-serif;font-size:14px;line-height:1.7;color:#111827;text-align:left;white-space:pre-wrap;overflow-wrap:break-word;word-break:normal">${root.innerHTML}</div>`;
  // Foglio A4 simulato: cornice grigio chiaro + foglio bianco centrato (max 640px) con margini interni.
  const sheet = `<table role="presentation" width="${SHEET}" cellpadding="0" cellspacing="0" border="0" align="center" bgcolor="#ffffff" style="border-collapse:separate;width:100%;max-width:${SHEET}px;margin:0 auto;background:#ffffff;border:1px solid #d1d5db;box-shadow:0 2px 8px rgba(15,23,42,0.08)"><tr><td align="left" style="padding:${PAD_Y}px ${PAD_X}px;vertical-align:top;text-align:left">${inner}</td></tr></table>`;
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#f3f4f6" style="border-collapse:collapse;width:100%;background:#f3f4f6"><tr><td align="center" style="padding:20px 10px">${sheet}</td></tr></table>`;
}

// Costruisce l'HTML interno (intestazione + corpo + firma) condiviso tra PDF e anteprima WYSIWYG.
// Cache in-memory dei PDF (base64) già generati: evita di rigenerare la stessa lettera
// (stesso contenuto/margini/intestazione) ad ogni invio. Chiave calcolata dal chiamante.
const _pdfB64Cache = new Map();
export function hashLetterKey(str) {
  let h = 5381;
  const s = String(str || "");
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}
export async function getCachedLetterPdfB64(key, generator) {
  if (key && _pdfB64Cache.has(key)) return _pdfB64Cache.get(key);
  const b64 = await generator();
  if (key && b64) {
    if (_pdfB64Cache.size > 15) _pdfB64Cache.clear();
    _pdfB64Cache.set(key, b64);
  }
  return b64;
}
export function invalidateLetterPdfCache(key) {
  if (key) _pdfB64Cache.delete(key); else _pdfB64Cache.clear();
}

export function buildLetterInnerHtml({ contentHtml, withHead, inte, signature }) {
  const head = withHead && inte && inte.html
    ? `<div class="pgp-letterhead">${restoreBlankLines(inte.html)}</div>`
    : withHead && inte
    ? `<div class="pgp-letterhead" style="text-align:${headAlign(inte)}"><div class="lh-name">${inte.nome}</div>${(inte.lines || []).map((l) => `<div class="lh-line">${l}</div>`).join("")}</div>`
    : "";
  const sig = signature ? `<div class="pgp-signature">${signature}</div>` : "";
  return `${head}<div class="pgp-doc">${applyJustifyTypography(restoreBlankLines(contentHtml))}</div>${sig}`;
}

// Stampa con lo stesso template della lettera (LETTER_CSS + buildLetterInnerHtml) su A4 con i margini indicati.
export function printLetterHtml(title, contentHtml, withHead, inte, margins) {
  const m = margins && typeof margins.top === "number" ? margins : { top: 25, right: 20, bottom: 25, left: 25 };
  const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`<html><head><title>${esc(title)}</title><meta charset="utf-8"/>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet"/>
    <style>@page{size:A4;margin:${m.top}mm ${m.right}mm ${m.bottom}mm ${m.left}mm}body{margin:0;background:#fff}${LETTER_CSS}</style>
    </head><body><div class="pgp-pdfroot">${buildLetterInnerHtml({ contentHtml, withHead, inte })}</div></body></html>`);
  w.document.close(); w.focus();
  setTimeout(() => w.print(), 400);
}

export async function downloadPdf(fileName, contentHtml, withHead, inte, margins, opts) {
  // margini documento ufficiale: sx 2.5cm, dx 2cm, sup 2.5cm, inf 2.5cm
  const m = margins && typeof margins.top === "number"
    ? { top: margins.top, right: margins.right, bottom: margins.bottom, left: margins.left }
    : { top: 25, right: 20, bottom: 25, left: 25 };
  const [{ jsPDF }, html2canvasMod] = await Promise.all([
    import("jspdf"),
    import("html2canvas"),
  ]);
  const html2canvas = html2canvasMod.default || html2canvasMod;
  const mmToPx = (mm) => (mm / 25.4) * 96;
  const contentWmm = 210 - m.left - m.right;
  const pxWidth = Math.round(mmToPx(contentWmm));
  const container = document.createElement("div");
  container.className = "pgp-pdfroot";
  container.style.cssText = `position:absolute;left:-10000px;top:0;width:${pxWidth}px;padding:0 0 24px;background:#fff;`;
  container.innerHTML = `<style>${LETTER_CSS}</style>${buildLetterInnerHtml({ contentHtml, withHead, inte, signature: opts?.signature })}`;
  document.body.appendChild(container);
  try {
    if (document.fonts && document.fonts.ready) { try { await document.fonts.ready; } catch (e) {} }
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    let markerPx = null;
    if (opts?.marker) {
      const needle = opts.marker.toLowerCase();
      const c = container.getBoundingClientRect();
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      let node;
      while (!markerPx && (node = walker.nextNode())) {
        const idx = (node.nodeValue || "").toLowerCase().indexOf(needle);
        if (idx < 0) continue;
        const range = document.createRange();
        range.setStart(node, idx); range.setEnd(node, idx + needle.length);
        const r = range.getBoundingClientRect();
        if (r.width > 0) markerPx = { x: r.left - c.left, w: r.width, y: r.bottom - c.top };
      }
      if (!markerPx) {
        const hits = Array.from(container.querySelectorAll("*")).filter((el) => (el.textContent || "").toLowerCase().includes(needle));
        const el = hits.find((h) => !Array.from(h.children).some((ch) => (ch.textContent || "").toLowerCase().includes(needle)));
        if (el) { const r = el.getBoundingClientRect(); markerPx = { x: r.left - c.left, w: r.width, y: r.bottom - c.top }; }
      }
    }
    const fullH = Math.ceil(container.scrollHeight);
    const scale = 2;
    const canvas = await html2canvas(container, {
      scale, backgroundColor: "#ffffff", useCORS: true, logging: false,
      windowWidth: pxWidth, width: pxWidth,
      height: fullH, windowHeight: fullH, scrollX: 0, scrollY: 0,
    });
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    pdf.setProperties({ keywords: encodeHtmlCopy(contentHtml) });
    const pageHmm = 297;
    const contentHmmPerPage = pageHmm - m.top - m.bottom;
    const pxPerMm = canvas.width / contentWmm;
    const pageHpx = Math.floor(contentHmmPerPage * pxPerMm);
    const sctx = canvas.getContext("2d");
    const isRowBlank = (y) => {
      if (y <= 0 || y >= canvas.height) return true;
      const data = sctx.getImageData(0, y, canvas.width, 1).data;
      for (let x = 0; x < canvas.width; x += 4) {
        const i = x * 4;
        if (data[i] < 245 || data[i + 1] < 245 || data[i + 2] < 245) return false;
      }
      return true;
    };
    // Altezza effettiva del contenuto: ignora le righe bianche finali (padding/spazi vuoti),
    // così il contenuto che sta in una pagina non genera una seconda pagina vuota.
    let contentBottom = canvas.height;
    while (contentBottom > 1 && isRowBlank(contentBottom - 1)) contentBottom--;
    const effectiveH = Math.max(1, contentBottom);
    const slices = [];
    let start = 0;
    const guard = Math.round(pageHpx * 0.18);
    while (start < effectiveH) {
      let end = Math.min(start + pageHpx, effectiveH);
      if (end < effectiveH) {
        let cut = end;
        for (let y = end; y > end - guard && y > start + 1; y--) {
          if (isRowBlank(y)) { cut = y; break; }
        }
        end = cut;
      }
      slices.push([start, end]);
      start = end;
    }
    for (let i = 0; i < slices.length; i++) {
      const [sy, ey] = slices[i];
      const sliceHpx = ey - sy;
      const pageCanvas = document.createElement("canvas");
      pageCanvas.width = canvas.width;
      pageCanvas.height = sliceHpx;
      const ctx = pageCanvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
      ctx.drawImage(canvas, 0, sy, canvas.width, sliceHpx, 0, 0, canvas.width, sliceHpx);
      const imgData = pageCanvas.toDataURL("image/jpeg", 0.95);
      const sliceHmm = sliceHpx / pxPerMm;
      if (i > 0) pdf.addPage();
      pdf.addImage(imgData, "JPEG", m.left, m.top, contentWmm, sliceHmm);
    }
    if (opts?.footer) {
      // Footer per pagina: linea sottile + "Elaborato con PatroGestPro" + numero pagina (es. 1/2), in basso a destra.
      const total = slices.length;
      const rightX = 210 - m.right;
      for (let pg = 1; pg <= total; pg++) {
        pdf.setPage(pg);
        pdf.setDrawColor(150, 150, 150);
        pdf.setLineWidth(0.2);
        pdf.line(m.left, 297 - 12, rightX, 297 - 12);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(7);
        pdf.setTextColor(120, 120, 120);
        pdf.text("Elaborato con PatroGestPro", rightX, 297 - 8, { align: "right" });
        pdf.text(`${pg}/${total}`, rightX, 297 - 4, { align: "right" });
      }
      pdf.setTextColor(0, 0, 0);
    }
    if (opts && opts.returnDoc) return pdf;
    if (opts && opts.returnBase64 && opts.marker) {
      let marker = null;
      if (markerPx) {
        const yc = markerPx.y * scale;
        const idx = slices.findIndex(([sy, ey]) => yc >= sy && yc <= ey);
        if (idx >= 0) marker = { page: idx, x_mm: m.left + (markerPx.x * scale) / pxPerMm, w_mm: (markerPx.w * scale) / pxPerMm, y_mm: m.top + (yc - slices[idx][0]) / pxPerMm };
      }
      return { b64: pdf.output("datauristring").split(",")[1], marker };
    }
    if (opts && opts.returnBase64) return pdf.output("datauristring").split(",")[1];
    pdf.save(fileName);
  } finally {
    document.body.removeChild(container);
  }
}
